import type { TechDiscipline } from "@/types/content";
import {
  AppWindow,
  Server,
  Smartphone,
  Cloud,
  Database,
  GitBranch,
  BrainCircuit,
} from "@/icons";

/**
 * Technologies explained by intent — why we choose each discipline, where it's
 * used and the business benefit. Deliberately not a logo wall.
 */
export const techDisciplines: TechDiscipline[] = [
  {
    category: "Frontend",
    icon: AppWindow,
    rationale:
      "We choose a component-driven, type-safe frontend so interfaces stay consistent, fast and cheap to evolve as the product grows.",
    whereUsed: "Marketing sites, dashboards, web apps and design systems.",
    benefit:
      "Faster pages, fewer bugs and a UI your team can extend without a rebuild.",
    items: ["React", "TypeScript", "Tailwind CSS", "Framer Motion", "Vite/Next"],
  },
  {
    category: "Backend",
    icon: Server,
    rationale:
      "We favour mature, well-typed backend frameworks that make correctness, testing and performance the default rather than an afterthought.",
    whereUsed: "APIs, business logic, integrations and data processing.",
    benefit:
      "Reliable services that scale predictably and are safe to change.",
    items: ["FastAPI", "Node.js", "Python", "GraphQL", "REST"],
  },
  {
    category: "Mobile",
    icon: Smartphone,
    rationale:
      "Cross-platform where it saves time and money; native where the experience demands it. We pick per project, not per trend.",
    whereUsed: "Customer apps, companion apps and internal field tools.",
    benefit:
      "One codebase where sensible, native quality where it matters.",
    items: ["React Native", "Expo", "Swift", "Kotlin"],
  },
  {
    category: "Cloud",
    icon: Cloud,
    rationale:
      "We deploy on proven cloud platforms with infrastructure-as-code, so environments are reproducible and scaling is boring.",
    whereUsed: "Hosting, edge delivery, storage and background jobs.",
    benefit:
      "Elastic scale, high availability and no surprise infrastructure debt.",
    items: ["AWS", "GCP", "Cloudflare", "Vercel"],
  },
  {
    category: "Data",
    icon: Database,
    rationale:
      "We match the datastore to the problem — relational for integrity, document for flexibility, in-memory for speed — never one tool for everything.",
    whereUsed: "Application data, search, caching and analytics.",
    benefit:
      "Data that's fast to query, safe to trust and ready to report on.",
    items: ["PostgreSQL", "MongoDB", "Redis", "Elasticsearch"],
  },
  {
    category: "DevOps",
    icon: GitBranch,
    rationale:
      "Automated pipelines, containers and monitoring mean every release is tested, repeatable and observable — not a leap of faith.",
    whereUsed: "CI/CD, environments, observability and releases.",
    benefit:
      "Ship faster with confidence and catch issues before your users do.",
    items: ["Docker", "Kubernetes", "CI/CD", "Terraform"],
  },
  {
    category: "AI",
    icon: BrainCircuit,
    rationale:
      "We integrate AI where it creates real leverage, with humans in the loop and clear guardrails — not novelty features that erode trust.",
    whereUsed: "Assistants, automation, search and content generation.",
    benefit:
      "Meaningful automation and insight without sacrificing control or trust.",
    items: ["OpenAI", "LangChain", "Vector DBs", "RAG pipelines"],
  },
];
